import CheckIcon from '@src/svgs/CheckIcon';
import { memo } from 'react';
import styled from 'styled-components';

import OrganizationAvatar from './OrganizationAvatar';
import {
  MENU_ICON_SIZE,
  MENU_ROW_INSET,
  MenuRow,
} from './OrganizationAvatarTooltip';
import SearchInput from './sidepanel/SearchInput';

import type { Showcase } from '@src/types.ts/story';

type OrganizationSwitcherProps = {
  organizations: Showcase[];
  selectedOrganization: Showcase;
  onSelect: (org: Showcase) => void;
  onSearch: (searchTerm: string) => void;
  initialSearchTerm?: string;
};

const PROJECT_LOGO_SIZE = 24;
/** Rows visible before the list scrolls. */
const VISIBLE_ROWS = 6;
const ROW_HEIGHT = 36;

/**
 * PLAT-12089 — every project is one row on one grid: name on the left edge
 * the rest of the menu shares, logo in a fixed-size slot, and a fixed slot for
 * the selected mark so switching projects moves only the check. The list is
 * kept in the order it came in; the selected project is not pinned to the top.
 */
function OrganizationSwitcher({
  organizations,
  onSelect,
  onSearch,
  selectedOrganization,
  initialSearchTerm,
}: OrganizationSwitcherProps) {
  return (
    <>
      <SearchField onClick={e => e.stopPropagation()}>
        <SearchInput
          iconRight={true}
          placeholder="Search project"
          handleAction={onSearch}
          radius="var(--radius-input)"
          width="100%"
          autoSubmitDelay={500}
          initialValue={initialSearchTerm}
        />
      </SearchField>
      <ProjectList $scrolls={organizations.length > VISIBLE_ROWS}>
        {organizations.map(org => {
          const selected = org.id === selectedOrganization.id;
          return (
            <ProjectRow
              key={org.id}
              $selected={selected}
              aria-current={selected ? 'true' : undefined}
              onClick={() => onSelect(org)}
            >
              <ProjectName title={org.title}>{org.title}</ProjectName>
              <ProjectLogo>
                <OrganizationAvatar
                  album={org}
                  username={null}
                  logoSize={PROJECT_LOGO_SIZE}
                  compact
                />
              </ProjectLogo>
              <SelectedSlot>
                {selected && (
                  <SelectedMark>
                    <CheckIcon
                      width="9"
                      height="9"
                      fillColor="var(--background)"
                      strokeColor="var(--background)"
                      strokeWidth="0.75"
                    />
                  </SelectedMark>
                )}
              </SelectedSlot>
            </ProjectRow>
          );
        })}
        {organizations.length === 0 && (
          <EmptyRow $static>No projects found</EmptyRow>
        )}
      </ProjectList>
    </>
  );
}

export default memo(OrganizationSwitcher);

const SearchField = styled.div`
  padding: 2px ${MENU_ROW_INSET} 6px;
`;

const ProjectList = styled.div<{ $scrolls: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-height: ${p => (p.$scrolls ? `${VISIBLE_ROWS * ROW_HEIGHT}px` : 'none')};
  overflow-y: ${p => (p.$scrolls ? 'auto' : 'visible')};
`;

const ProjectRow = styled(MenuRow)<{ $selected: boolean }>`
  min-height: ${ROW_HEIGHT}px;
  /* Selected value is gold, as in every menu — never green, which is for
     actions (design-system "Menu-item styling"). */
  color: ${p => (p.$selected ? 'var(--accent)' : 'var(--text-secondary)')};

  &:hover {
    color: ${p => (p.$selected ? 'var(--accent)' : 'var(--foreground)')};
  }
`;

const ProjectName = styled.span`
  flex: 1;
  min-width: 0;
  font-size: 13px;
  font-weight: var(--font-weight-medium);
  line-height: 1.2;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ProjectLogo = styled.span`
  display: inline-flex;
  width: ${PROJECT_LOGO_SIZE}px;
  height: ${PROJECT_LOGO_SIZE}px;
  flex-shrink: 0;
`;

/** Fixed slot so unselected rows keep their logo in place. */
const SelectedSlot = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${MENU_ICON_SIZE}px;
  height: ${MENU_ICON_SIZE}px;
  flex-shrink: 0;
`;

const SelectedMark = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${MENU_ICON_SIZE}px;
  height: ${MENU_ICON_SIZE}px;
  border-radius: 50%;
  background: var(--accent);
`;

const EmptyRow = styled(MenuRow)`
  min-height: ${ROW_HEIGHT}px;
  color: var(--text-muted);
  font-size: 13px;
`;
